import React, { useCallback, useEffect, useState } from 'react';
import InspectionToolbar from './Toolbar/inspectionToolbar';
import VehicleInspectionRecordTable from './Table/VehicleInspectionRecordTable';
import Pagination from 'reusable/pagination';
import { PAGE_SIZE } from 'config/constant';
import axiosInstance from 'config/axiosInstance';
import { businessRangeQuery } from 'config/time';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';

const VehicleInspectionManagement: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [inspectionRecordData, setInspectionRecordData] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [failedInspection, setFailedInspection] = useState<boolean | null>(
    null
  );
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
    setCurrentPage(1);
  };

  const handleFailedInspectionChange = (value: boolean | null) => {
    setFailedInspection(value);
    setCurrentPage(1);
  };

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      // サーバーの条件名で送る。以前は pageSize・sort・startDate/endDate を送っており、
      // 期間・並び替え・1ページの件数が効いていなかった。
      let apiUrl = '/v1/vehicle_inspection_records/?';
      if (dateRange.startDate && dateRange.endDate) {
        apiUrl += businessRangeQuery('create_time', dateRange.startDate, dateRange.endDate);
      }
      apiUrl += `page=${currentPage}&page_size=${PAGE_SIZE}&search=${searchValue}&order_by=${sortValue}`;
      if (failedInspection !== null) {
        apiUrl += `&inspecion_result=${failedInspection}`;
      }
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      setInspectionRecordData(response.data.results);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch inspection records', error);
      setFetchError(error);
    }
  }, [searchValue, sortValue, currentPage, dateRange, failedInspection, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section className="flex flex-col space-y-4">
      {/* Toolbar */}
      <div>
        <InspectionToolbar
          searchValue={searchValue}
          sortValue={sortValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onDateRangeChange={handleDateRangeChange}
          onFailedInspectionChange={handleFailedInspectionChange}
          exportData={inspectionRecordData}
        />
      </div>
      {/* Table */}
      <div>
        {fetchError ? (
          <FetchFailed error={fetchError} onRetry={fetchData} />
        ) : (
          <VehicleInspectionRecordTable inspectionRecord={inspectionRecordData} />
        )}
      </div>
      {/* Pagination */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </section>
  );
};

export default VehicleInspectionManagement;
